import * as React from 'react'
import { matchRoutes, useLocation, useSearchParams } from 'react-router-dom'
import routes from '../routes'
import { UserContext } from '../UserContext'
import useMediaQuery from '@mui/material/useMediaQuery'
import { VIEW_TYPES, deriveViewType } from '../utils/viewType'
import { isKeywordGraphApp, appProductName } from '../appVariant'

export default function usePageInfo(route = {}) {
    const [pageInfo, setPageInfo] = React.useState({})

    const location = useLocation()

    let [searchParams, setSearchParams] = useSearchParams()

    const { username = '' } = React.useContext(UserContext)

    const isMobile = useMediaQuery('(max-width:600px)')

    React.useEffect(() => {
        const routerMatch = matchRoutes(
            [
                { path: routes.main },
                { path: routes.secondary },
                { path: routes.graph },
            ],
            location
        ).filter(Boolean)[0]

        const { params } = routerMatch

        const isViewingGraph = params['*'] ? true : false

        if (!params) return

        // Single source of truth for view-type derivation — also used by
        // the bootstrap store. See views/react/utils/viewType.js.
        // `username` from UserContext defaults to '' when anonymous; the
        // helper normalises falsy → null internally.
        const viewType = deriveViewType({
            mainPage: params.mainPage,
            secondPage: params.secondPage,
            isViewingGraph,
            searchParams,
            loggedInUsername: username,
        })
        const viewingTopSynthesis =
            viewType === VIEW_TYPES.SYNTHESIS ||
            viewType === VIEW_TYPES.CONCEPTS_SYNTHESIS

        // Is this a /:owner/:graph context view? In react-router 6 the graph
        // route (`/:userName/:graphName/*`) does NOT match a 2-segment path,
        // so `/:user/:graph` resolves to the `/:mainPage/:secondPage` route
        // and `params.graphName` is undefined — only `params.secondPage` is
        // set. The old code only set location:'context' when
        // `params.mainPage === username` (line below), which is true for an
        // anonymous viewer (username falls back to the URL owner) but FALSE
        // for a logged-in user viewing someone else's graph — so their view
        // fell through to the 'apps' fallback and LazyGraph never mounted.
        // Key off the authoritative viewType instead of username matching.
        const isGraphContextView =
            !!params.graphName ||
            (viewType === VIEW_TYPES.CONTEXT && !!params.secondPage)

        const servicePages = [
            'evernote',
            'import',
            'subscription',
            'upgrade',
            'api-access',
            'apps',
            'settings',
            'admin-panel',
        ]

        switch (params.mainPage) {
            case 'import':
                setPageInfo({ label: `${appProductName()} Import` })
                if (params.secondPage == 'evernote') {
                    setPageInfo({
                        label: 'Import Evernote Notebooks',
                        selected: 'evernote',
                        location: 'evernote',
                    })
                    break
                }
                if (!params.secondPage) {
                    setPageInfo({
                        label: `${appProductName()} Text Import Tools`,
                        selected: 'import',
                        location: 'import',
                    })
                }
                break
            case 'subscription':
                setPageInfo({
                    label: 'Your InfraNodus Subscription',
                    selected: 'subscription',
                    location: 'subscription',
                })
                break
            case 'upgrade':
                setPageInfo({
                    label: `Upgrade ${appProductName()}`,
                    selected: 'upgrade',
                    location: 'upgrade',
                })
                break
            case 'api-access':
                setPageInfo({
                    label: 'InfraNodus API',
                    selected: 'api-access',
                    location: 'api-access',
                })
                break
            case 'apps':
                setPageInfo({
                    label: !isMobile
                        ? isKeywordGraphApp()
                            ? `Welcome to KeywordGraph, ${username}`
                            : `Welcome to InfraNodus, ${username}`
                        : isKeywordGraphApp()
                          ? 'Welcome to KeywordGraph'
                          : 'Welcome to InfraNodus',
                    selected: 'apps',
                    location: 'apps',
                })
                break
            case 'settings':
                setPageInfo({
                    label: !isMobile
                        ? `InfraNodus Global Settings for: ${username}`
                        : 'Global Settings',
                    selected: 'settings',
                    location: 'settings',
                })
                break
            case 'admin-panel':
                setPageInfo({
                    label: 'Admin Panel',
                    selected: 'admin-panel',
                    location: 'admin-panel',
                })
                break
            default:
                // A /:owner/:graph URL has no `mainPage` service value, so it
                // falls here. Don't apply the logged-in "fall back to apps"
                // shortcut when we're actually viewing a graph — otherwise a
                // logged-in viewer of someone else's public graph gets
                // location:'apps' (a service page), which the early-return
                // below makes sticky, so `isOnGraphPage` stays false and
                // LazyGraph never mounts (blank graph). Anonymous viewers
                // dodged this only because `username` is falsy.
                if (!viewingTopSynthesis && username && !isGraphContextView) {
                    setPageInfo({
                        label: 'InfraNodus',
                        selected: 'apps',
                        location: 'apps',
                    })
                }
        }

        if (pageInfo && servicePages.includes(pageInfo.location)) return

        const queryParams = {}
        searchParams.forEach((value, key) => (queryParams[key] = value))

        if (viewingTopSynthesis) {
            setPageInfo(
                viewType === VIEW_TYPES.SYNTHESIS
                    ? {
                          label: 'Top Graphs Synthesis',
                          selected: params.graphName,
                          location: 'synthesis',
                      }
                    : {
                          label: 'Top Concepts Synthesis',
                          selected: params.graphName,
                          location: 'conceptsSynthesis',
                      }
            )
            return
        }

        if (viewType === VIEW_TYPES.SEARCH) {
            setPageInfo({
                label: 'Search All Graphs',
                selected: params.graphName,
                location: 'search',
            })
            return
        }

        // A graph context view: 3-segment editor URLs expose `graphName`;
        // 2-segment /:owner/:graph URLs expose `secondPage` (see
        // `isGraphContextView`). This must NOT gate on `mainPage === username`
        // — that excluded logged-in viewers of someone else's public graph.
        if (isGraphContextView) {
            const graphName = params.graphName || params.secondPage
            setPageInfo({
                label: graphName,
                selected: graphName,
                location: 'context',
            })
            return
        }
    }, [location, username])

    return pageInfo
}
